// Filtrado, orden, píldoras y ficha de la pantalla «Teléfonos» (`portal-vendedor-catalogo.html`,
// solo equipos PHONE). Funciones puras; la búsqueda y las métricas se reusan de `catalog/lib`.
import type { CatalogItem, PlanLevel } from '@/domains/sales/wizard/types';
import { isSoldOut, stockCount } from '@/domains/sales/wizard/catalogAvailability';
import {
    EMPTY_FILTERS,
    filterCatalog,
    hasPromotion,
    isTopSeller,
    sortCatalog,
    stockToneOf,
    type CatalogExplorerFilters,
    type SortKey,
} from '../../catalog/lib/catalogExplorer';
export { financierChips, type FinancierChip } from '../../pos/lib/financierChips';
export {
    availableColors,
    hasPromotion,
    isTopSeller,
    phoneCategoryLabel,
    priorityLabel,
    promotionLabel,
    TOP_SALES_PRIORITY_MIN,
} from '../../catalog/lib/catalogExplorer';
import { acceptsSurekeep, isEligible, type LevelPlan } from './levelPlans';

export interface PhoneFilters extends CatalogExplorerFilters {
    /** Nivel cuyo plan tiene que alcanzar al equipo; `''` es «Todos». */
    level: PlanLevel | '';
}

export const EMPTY_PHONE_FILTERS: PhoneFilters = { ...EMPTY_FILTERS, level: '' };

export type PhoneStatFilter = 'all' | 'top' | 'promo' | 'low' | 'out';

export type PhoneSortKey = SortKey;

export interface PhoneSortState {
    key: PhoneSortKey;
    direction: 'asc' | 'desc';
}

/** El mock abre ordenado por prioridad de venta, de mayor a menor. */
export const DEFAULT_PHONE_SORT: PhoneSortState = { key: 'priority', direction: 'desc' };

/** Un clic en la columna activa invierte; en otra, empieza ascendente. */
export function nextPhoneSort(current: PhoneSortState, key: PhoneSortKey): PhoneSortState {
    if (current.key !== key) return { key, direction: 'asc' };
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

export function isPhone(item: CatalogItem): boolean {
    return item.category === 'PHONE';
}

export function countActivePhoneFilters(filters: PhoneFilters): number {
    return [
        filters.query.trim(),
        filters.brand,
        filters.minPrice,
        filters.maxPrice,
        filters.financier,
        filters.level,
    ].filter((value) => value !== '').length;
}

function matchesStat(item: CatalogItem, stat: PhoneStatFilter): boolean {
    switch (stat) {
        case 'top':
            return isTopSeller(item);
        case 'promo':
            return hasPromotion(item);
        case 'low':
            return stockToneOf(item) === 'low';
        case 'out':
            return isSoldOut(item);
        default:
            return true;
    }
}

/** Sin planes legibles el filtro de nivel no puede decidir: se ignora en vez de vaciar la tabla. */
export function filterPhones(
    items: CatalogItem[],
    filters: PhoneFilters,
    stat: PhoneStatFilter,
    plans: LevelPlan[] | null,
): CatalogItem[] {
    const levelPlan =
        filters.level === '' || plans === null
            ? null
            : (plans.find((plan) => plan.level === filters.level) ?? null);
    return filterCatalog(items, filters, 'all').filter(
        (item) => matchesStat(item, stat) && (levelPlan === null || isEligible(item, levelPlan)),
    );
}

const byPriority = (a: CatalogItem, b: CatalogItem): number =>
    (a.salePriority ?? -1) - (b.salePriority ?? -1);

/** Ordena sin mutar; en prioridad, el empate se rompe alfabético por marca y modelo. */
export function sortPhones(items: CatalogItem[], sort: PhoneSortState): CatalogItem[] {
    if (sort.key !== 'priority')
        return sortCatalog(items, { key: sort.key, direction: sort.direction });
    const sign = sort.direction === 'asc' ? 1 : -1;
    const alphabetical = sortCatalog(items, { key: 'brand', direction: 'asc' });
    return [...alphabetical].sort((a, b) => sign * byPriority(a, b));
}

export interface PhoneStats {
    all: number;
    top: number;
    promo: number;
    low: number;
    out: number;
}

export function phoneStats(items: CatalogItem[]): PhoneStats {
    return {
        all: items.length,
        top: items.filter(isTopSeller).length,
        promo: items.filter(hasPromotion).length,
        low: items.filter((item) => stockToneOf(item) === 'low').length,
        out: items.filter(isSoldOut).length,
    };
}

export interface PhoneMetrics {
    avgPriceCents: number | null;
    stockTotal: number;
    surekeepCount: number;
    count: number;
}

export function phoneMetrics(rows: CatalogItem[]): PhoneMetrics {
    const total = rows.reduce((sum, item) => sum + item.cashPriceCents, 0);
    return {
        avgPriceCents: rows.length === 0 ? null : Math.round(total / rows.length),
        stockTotal: rows.reduce((sum, item) => sum + stockCount(item), 0),
        surekeepCount: rows.filter(acceptsSurekeep).length,
        count: rows.length,
    };
}
